import { useState, createContext, useContext, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error("useConfirm must be used within ConfirmProvider");
  return context;
};

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, title: "", description: "", resolve: null });

  const confirm = useCallback((title, description) => {
    return new Promise((resolve) => {
      setState({ open: true, title, description, resolve });
    });
  }, []);

  const handleConfirm = () => {
    state.resolve?.(true);
    setState({ open: false, title: "", description: "", resolve: null });
  };

  const handleCancel = () => {
    state.resolve?.(false);
    setState({ open: false, title: "", description: "", resolve: null });
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <AlertDialogContent className="bg-card border-border" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-foreground">{state.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">{state.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border rounded-sm" data-testid="confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} className="bg-destructive text-destructive-foreground rounded-sm" data-testid="confirm-action">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};
